import { Box, Typography, IconButton, Button, TextField } from '@mui/material';
import ThumbDownAltOutlinedIcon from '@mui/icons-material/ThumbDownAltOutlined';
import KeyboardBackspaceOutlinedIcon from '@mui/icons-material/KeyboardBackspaceOutlined';
import { FeedbackType, PostCallFeedbackState } from './types';
import { INITIAL_POST_CALL_FEEDBACK_STATE } from './initialPostCallFeedbackState.const';
import FeedbackActions from './FeedbackActions';
import { StringHelper } from '@act/shared/utilities';
import { useTracking } from '../../../tracking';

const ISSUES_MAP: Record<FeedbackType, string[]> = {
  summary: ['missing-words', 'missing-topics'] as const,
  suggestions: [
    'incorrect-information',
    'duplicate',
    'missing-context',
  ] as const,
  transcription: [
    'missing-words',
    'missing-topics',
    'only-one-speaker',
  ] as const,
} as const;

interface NegativeQuestionProps {
  feedbackType: FeedbackType;
  postCallFeedback: PostCallFeedbackState;
  isUpdatingPostCallFeedback: boolean;
  setPostCallFeedback: (postCallFeedback: PostCallFeedbackState) => void;
  onClose: () => void;
  submitFeedback: (feedback?: PostCallFeedbackState) => Promise<void>;
  compact?: boolean;
}

const NegativeQuestion = ({
  feedbackType,
  postCallFeedback,
  isUpdatingPostCallFeedback,
  setPostCallFeedback,
  onClose,
  submitFeedback,
  compact = false,
}: NegativeQuestionProps) => {
  const isSummaryFeedback = feedbackType === 'summary';
  const isSuggestionsFeedback = feedbackType === 'suggestions';

  const feedbackValue = isSummaryFeedback
    ? postCallFeedback.summaryAccuracyFeedback
    : isSuggestionsFeedback
    ? postCallFeedback.suggestionAccuracyFeedback
    : postCallFeedback.accuracyFeedback;

  const tracking = useTracking();
  const { generateTrackingProps } = tracking || {};

  function goBack() {
    feedbackType === 'suggestions' && onClose();
    setPostCallFeedback(INITIAL_POST_CALL_FEEDBACK_STATE);
  }

  function getVariant(issue: string) {
    let issuesArray: string[] | undefined;
    if (isSummaryFeedback) {
      issuesArray = postCallFeedback.summaryAccuracyIssues;
    } else if (isSuggestionsFeedback) {
      issuesArray = postCallFeedback.suggestionAccuracyIssues;
    } else {
      issuesArray = postCallFeedback.accuracyIssues;
    }

    if (issuesArray?.includes(issue)) {
      return 'contained';
    }
    return 'outlined';
  }

  function toggleIssue(issue: string) {
    const updatedFeedback = { ...postCallFeedback };

    if (isSummaryFeedback) {
      if (updatedFeedback.summaryAccuracyIssues?.includes(issue)) {
        updatedFeedback.summaryAccuracyIssues =
          updatedFeedback.summaryAccuracyIssues?.filter(i => i !== issue);
      } else {
        updatedFeedback.summaryAccuracyIssues = [
          ...(updatedFeedback.summaryAccuracyIssues || []),
          issue,
        ];
      }
    } else if (isSuggestionsFeedback) {
      if (updatedFeedback.suggestionAccuracyIssues?.includes(issue)) {
        updatedFeedback.suggestionAccuracyIssues =
          updatedFeedback.suggestionAccuracyIssues?.filter(i => i !== issue);
      } else {
        updatedFeedback.suggestionAccuracyIssues = [
          ...(updatedFeedback.suggestionAccuracyIssues || []),
          issue,
        ];
      }
    } else {
      if (updatedFeedback.accuracyIssues?.includes(issue)) {
        updatedFeedback.accuracyIssues = updatedFeedback.accuracyIssues?.filter(
          i => i !== issue
        );
      } else {
        updatedFeedback.accuracyIssues = [
          ...(updatedFeedback.accuracyIssues || []),
          issue,
        ];
      }
    }

    setPostCallFeedback(updatedFeedback);
  }

  const handleFeedbackChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const updatedFeedback = { ...postCallFeedback };
    const newFeedbackValue = e.target.value;

    if (isSummaryFeedback) {
      updatedFeedback.summaryAccuracyFeedback = newFeedbackValue;
    } else if (isSuggestionsFeedback) {
      updatedFeedback.suggestionAccuracyFeedback = newFeedbackValue;
    } else {
      updatedFeedback.accuracyFeedback = newFeedbackValue;
    }

    setPostCallFeedback(updatedFeedback);
  };

  return (
    <Box sx={{ width: '100%' }}>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: compact ? '4px' : '8px',
          width: '100%',
          marginBottom: compact ? '4px' : '8px',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            gap: compact ? '4px' : '6px',
            flexWrap: 'wrap',
          }}
        >
          {ISSUES_MAP[feedbackType].map(issue => (
            <Button
              key={issue}
              size="small"
              variant={getVariant(issue)}
              sx={
                compact
                  ? {
                      minWidth: 'auto',
                      padding: '4px 8px',
                      fontSize: '0.75rem',
                      height: '28px',
                    }
                  : {}
              }
              {...(generateTrackingProps
                ? generateTrackingProps(`${issue}-issue-button`)
                : {})}
              onClick={() => toggleIssue(issue)}
            >
              {StringHelper.sentenceCase(issue, true)}
            </Button>
          ))}
        </Box>
        <Box>
          <TextField
            label="Feedback (optional)"
            sx={{
              width: '100%',
              ...(compact && {
                '& .MuiInputBase-root': {
                  fontSize: '0.875rem',
                },
                '& textarea': {
                  padding: '6px 8px !important',
                  minHeight: '32px !important',
                  maxHeight: '48px',
                },
                '& .MuiInputLabel-root': {
                  fontSize: '0.75rem',
                },
              }),
            }}
            multiline
            rows={compact ? 1 : 2}
            size="small"
            value={feedbackValue}
            onChange={handleFeedbackChange}
            {...(generateTrackingProps
              ? generateTrackingProps('feedback-text-input')
              : {})}
          />
        </Box>
      </Box>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: compact ? '4px' : '8px',
        }}
      >
        <IconButton
          sx={compact ? { padding: '4px' } : {}}
          {...(generateTrackingProps
            ? generateTrackingProps('go-back-button')
            : {})}
          disabled={isUpdatingPostCallFeedback}
          onClick={goBack}
        >
          <KeyboardBackspaceOutlinedIcon
            color="info"
            sx={compact ? { fontSize: '18px' } : {}}
          />
        </IconButton>

        <Typography variant="body2" sx={compact ? { fontSize: '0.75rem' } : {}}>
          What went wrong?
        </Typography>

        <IconButton disabled={true} sx={compact ? { padding: '4px' } : {}}>
          <ThumbDownAltOutlinedIcon
            color="error"
            sx={compact ? { fontSize: '18px' } : {}}
          />
        </IconButton>

        <Box sx={{ marginLeft: 'auto' }}>
          <FeedbackActions
            feedbackType={feedbackType}
            onClose={onClose}
            submitFeedback={submitFeedback}
            isUpdatingPostCallFeedback={isUpdatingPostCallFeedback}
            postCallFeedback={postCallFeedback}
          />
        </Box>
      </Box>
    </Box>
  );
};

export default NegativeQuestion;
